import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BroadcastChannel } from "broadcast-channel";
import { BroadcastChannelName } from "@web-src/features/app/types";
import { getAppId } from "@web-src/features/app/utils";
import {
  logout as logoutDispatch,
  selectAuthState,
} from "@web-src/features/auth/authSlice";
import { useAppDispatch } from "@web-src/store";
import { getSessionId } from "@web-src/utils/session";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import { useUserLogoutMutation } from "@web-src/features/auth/authApi";
import { DeviceType } from "@web-src/features/auth/types";
import { Alert, PageLoaderFull } from "@jugl-web/ui-components/cross-platform";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { useSelector } from "react-redux";
import {
  JUGL_LOGIN,
  LOGIN_FLOW_KEY,
  TIMEZONE_MISMATCH_ALERT_SHOWN,
} from "@jugl-web/utils/storage";
import { UserAuthContext, UserAuthContextValue } from "./UserAuthContext";

interface AuthBroadcastChannelMessage {
  emitterId: string;
}

const channel: BroadcastChannel<AuthBroadcastChannelMessage> =
  new BroadcastChannel(BroadcastChannelName.auth);

export const UserAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const [userLogout, { isLoading }] = useUserLogoutMutation();
  const { navigateToPage } = useNavigation();
  const { t } = useTranslations();
  const { isCx } = useAppVariant();
  const emitterId = getSessionId();

  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const onAfterLogoutRef = React.useRef<(() => void) | undefined>();

  useEffect(() => {
    const messageHandler = (e: AuthBroadcastChannelMessage) => {
      if (emitterId !== e.emitterId) {
        window.location.reload();
      }
    };
    channel.addEventListener("message", messageHandler);
    return () => {
      channel.removeEventListener("message", messageHandler);
    };
  }, [emitterId]);

  const logoutSideEffects = useCallback(async () => {
    await dispatch(logoutDispatch());
    setIsAlertVisible(false);
    navigateToPage(isCx ? "cxLogin" : "authLogin", undefined, {
      replace: true,
    });
    channel.postMessage({
      emitterId,
    });
    localStorage.removeItem(JUGL_LOGIN);
    localStorage.removeItem(TIMEZONE_MISMATCH_ALERT_SHOWN);
  }, [dispatch, emitterId, isCx, navigateToPage]);

  const handleLogout = useCallback(async () => {
    await userLogout({
      app_id: getAppId(),
      device_type: DeviceType.web,
    });
    logoutSideEffects();
    onAfterLogoutRef.current?.();
    onAfterLogoutRef.current = undefined;
  }, [logoutSideEffects, userLogout]);

  const logout = useCallback(
    (showAlert: boolean, onAfterLogout?: () => void) => {
      onAfterLogoutRef.current = onAfterLogout;
      return showAlert ? setIsAlertVisible(true) : handleLogout();
    },
    [handleLogout]
  );

  const contextValue = useMemo<UserAuthContextValue>(
    () => ({ logout, logoutSideEffects }),
    [logout, logoutSideEffects]
  );
  const { isAuthenticated } = useSelector(selectAuthState);
  useEffect(() => {
    if (isAuthenticated) {
      localStorage.removeItem(LOGIN_FLOW_KEY);
    }
  }, [isAuthenticated]);

  return (
    <UserAuthContext.Provider value={contextValue}>
      {children}
      <Alert
        title={t({
          id: "common.logout",
          defaultMessage: "Logout",
        })}
        isOpen={isAlertVisible}
        onRequestClose={() => setIsAlertVisible(false)}
        content={t({
          id: "logout-provider.logout-confirmation-message",
          defaultMessage: "Are you sure you want to log out?",
        })}
        className="drop-shadow-xl"
        backdropClassName="bg-transparent"
        buttons={[
          {
            text: t({
              id: "common.cancel",
              defaultMessage: "Cancel",
            }),
            role: "close",
          },
          {
            text: t({
              id: "common.logout",
              defaultMessage: "Logout",
            }),
            isDisabled: isLoading,
            onClick: handleLogout,
          },
        ]}
      />
      <PageLoaderFull isActive={isLoading} isTransparent />
    </UserAuthContext.Provider>
  );
};
